import type { ReactNode } from "react";
import classNames from "classnames";
import {
    AvatarGroup,
    type AvatarGroupProps,
} from "@design-system/Avatar/AvatarGroup";
import styles from "./LabelledAvatarGroup.module.scss";

interface LabelledAvatarGroupProps<T> extends AvatarGroupProps<T> {
    /** Trailing text after the stack, e.g. "3 entities". */
    label?: ReactNode;
}

/**
 * An `AvatarGroup` followed by an optional label, shared by the entity, class
 * and workspace groups. The label inherits the surrounding text style, so it
 * reads like a trigger's or cell's own text. `className` lands on the
 * outermost element. An empty group with a label shows the label alone.
 */
export const LabelledAvatarGroup = <T,>({
    label,
    className,
    ...groupProps
}: Readonly<LabelledAvatarGroupProps<T>>) => {
    if (label === undefined) {
        return <AvatarGroup {...groupProps} className={className} />;
    }

    return (
        <span className={classNames(styles.labelled, className)}>
            {groupProps.items.length > 0 && <AvatarGroup {...groupProps} />}
            <span>{label}</span>
        </span>
    );
};
